import {
  readDatetimeFieldNames,
  readDatetimeFields,
} from "@/helpers/timezone";

export class GptDatasetMapper {
  static mapBackendToFrontend(datasetData) {
    // The backend keeps these identical across entries, so the first one speaks for all.
    const cloudConfig = (datasetData?.cloud_sources || [])[0] || {};
    return {
      datasetName: datasetData.name,
      indexColumnName: datasetData.idx_column_name,
      secondaryIndex: datasetData.secondary_idx_column,
      fillUpEmptyIndex: datasetData.is_idx_fillup_if_empty,
      imageColumn: datasetData.image_url_column,
      description: datasetData.description,
      datasetFile: null,
      uploadSource: (datasetData?.cloud_sources || []).length ? "cloud" : "local",
      cloudProvider: cloudConfig.provider || "",
      cloudSources: (datasetData?.cloud_sources || []).map((s) => ({
        provider: s.provider,
        account_id: s.account_id,
        items: s.items || (s.item_ids || []).map((id) => ({ id, name: id, _restored: true })),
      })),
      cloudSourcesDirty: false,
      cloudUpdateFrequency: cloudConfig.cloud_update_frequency || "",
      cloudSyncHours: cloudConfig.cloud_sync_hours ?? null,
      cloudExtensions: cloudConfig.cloud_extensions || [],
      datasetId: datasetData.dataset_id,
      tags: datasetData.tags ?? [],
      filterableFields: datasetData.filterable_fields ?? [],
      is_ocr: datasetData.is_ocr || false,
      is_text_extraction: datasetData.is_text_extraction !== false,
      metadata: datasetData.metadata || "",
      urls: datasetData.urls || [],
      socialMediaType: datasetData.social_media_type,
      socialMediaTrendsLimit: datasetData.social_media_trends_limit,
      socialMediaPostsPerTrend: datasetData.social_media_posts_per_trend,
      countryCode: datasetData.country_code,
      updateFrequency: datasetData.update_frequency,
      numOfDays: datasetData.n_days,
      scrapingMode: datasetData.scraping_mode,
      maxDepth: datasetData.max_depth,
      dataset_type: datasetData.dataset_type || "",
      master_timezone: datasetData.master_timezone ?? null,
      naive_policy: datasetData.naive_policy ?? null,
      aware_policy: datasetData.aware_policy ?? null,
      datetime_fields: readDatetimeFieldNames(datasetData),
      datetime_fields_tz: readDatetimeFields(datasetData),
      primary_datetime_field: datasetData.primary_datetime_field || "",
    };
  }

  static mapFrontendToBackend(datasetData) {
    const optionalDataMapper = {
      name: "datasetName",
      idx_column_name: "indexColumnName",
      secondary_idx_column: "secondaryIndex",
      image_url_column: "imageColumn",
      description: "description",
      dataset_file: "datasetFile",
      is_ocr: "is_ocr",
      is_text_extraction: "is_text_extraction",
      metadata: "metadata",
      is_idx_fillup_if_empty: "fillUpEmptyIndex",
      tags: "tags",
      filterable_fields: "filterableFields",
      urls: "urls",
      social_media_type: "socialMediaType",
      social_media_trends_limit: "socialMediaTrendsLimit",
      social_media_posts_per_trend: "socialMediaPostsPerTrend",
      country_code: "countryCode",
      max_depth: "maxDepth",
      n_days: "numOfDays",
      update_frequency: "updateFrequency",
      scraping_mode: "scrapingMode",
      primary_datetime_field: "primary_datetime_field",
    };
    const formData = {};
    for (const key in optionalDataMapper) {
      if (Object.prototype.hasOwnProperty.call(optionalDataMapper, key)) {
        const value = datasetData[optionalDataMapper[key]];
        if (["is_idx_fillup_if_empty", "is_ocr", "is_text_extraction"].includes(key) && typeof value === "boolean") {
          formData[key] = value;
        } else if (Array.isArray(value)) {
          formData[key] = value;
        } else if (value || value === null) {
          formData[key] = value;
        }
      }
    }
    if (datasetData.uploadSource === "cloud") {
      const frequency = datasetData.cloudUpdateFrequency || "once";
      formData.cloud_update_frequency = frequency;
      formData.cloud_sync_hours =
        frequency === "custom" && datasetData.cloudSyncHours ? Number(datasetData.cloudSyncHours) : null;
      formData.cloud_extensions = datasetData.cloudExtensions || [];
    }

    // Time-series only, kept out of the mapper above so other requests stay untouched.
    const datetimeFieldsTz = datasetData.datetime_fields_tz
    const hasDatetimeFieldsTz = Array.isArray(datetimeFieldsTz) && datetimeFieldsTz.length > 0
    if (datasetData.dataset_type === 'time_series' || hasDatetimeFieldsTz) {
      if (hasDatetimeFieldsTz) {
        formData.datetime_fields_tz = datetimeFieldsTz.map(field => ({
          name: field.name,
          timezone: field.timezone ?? null,
        }))
      }
      formData.master_timezone = datasetData.master_timezone ?? null
      formData.naive_policy = datasetData.naive_policy ?? null
      formData.aware_policy = datasetData.aware_policy ?? null
    }

    return formData;
  }
}
